import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Joi from 'joi';

import { texts } from 'utils/languages/fr';
import { getFormattedAddress, validateSchema } from 'utils/helpers';

import { MemoizedIndexedCountriesSelect } from 'containers/tunnel/steps/contact-info/memoized-components/MemoizedComponents';
import { ActionButton, CancelButton, CustomDialogTitle, CustomTextField } from 'components';
import { Dialog, DialogActions, DialogContent } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles(() => ({
    field: { width: '100%' },
}));

const ManualAddressDialog = props => {
    const { open, handleClose, onAddressChange, title = texts.MANUAL_ADDRESS } = props;
    const classes = useStyles();

    const [errors, setErrors] = useState({});
    const [address, setAddress] = useState({
        address: '',
        city: '',
        zipCode: '',
        country: 'FR',
    });

    const schema = useMemo(
        () => ({
            address: Joi.string().required().messages({
                'string.empty': texts.SAISIR_LE_CHAMP,
            }),
            city: Joi.string().required().messages({
                'string.empty': texts.SAISIR_LE_CHAMP,
            }),
            zipCode: Joi.string().required().messages({
                'string.empty': texts.SAISIR_LE_CHAMP,
            }),
        }),
        []
    );

    const formIsValid = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, address);
        setErrors(newErrors);

        return valid;
    }, [schema, address]);

    const handleChange = useCallback(e => {
        const { name, value } = e.target;
        setAddress(prevState => ({ ...prevState, [name]: value }));
    }, []);

    const handleSubmit = useCallback(() => {
        if (formIsValid()) {
            onAddressChange(getFormattedAddress(address));
            handleClose();
        }
    }, [onAddressChange, address, formIsValid, handleClose]);

    useEffect(() => {
        if (!open) {
            setAddress({
                address: '',
                city: '',
                zipCode: '',
                country: 'FR',
            });
            setErrors({});
        }
    }, [open]);

    return (
        <Dialog open={open}>
            <CustomDialogTitle title={title} handleClose={handleClose} />
            <DialogContent>
                <CustomTextField
                    name="address"
                    inputStyle={classes.field}
                    label={texts.ADDRESS}
                    value={address.address}
                    onChange={handleChange}
                    error={errors.address}
                    showError
                />
                <CustomTextField
                    name="city"
                    inputStyle={classes.field}
                    label={texts.VILLE}
                    value={address.city}
                    onChange={handleChange}
                    error={errors.city}
                    showError
                />
                <CustomTextField
                    name="zipCode"
                    inputStyle={classes.field}
                    label={texts.CODE_POSTAL}
                    value={address.zipCode}
                    onChange={handleChange}
                    error={errors.zipCode}
                    showError
                />
                <MemoizedIndexedCountriesSelect
                    name="country"
                    inputStyle={classes.field}
                    value={address.country}
                    onChange={handleChange}
                    error={errors.country}
                    showError
                />
            </DialogContent>
            <DialogActions>
                <CancelButton onClick={handleClose}>{texts.CANCEL}</CancelButton>
                <ActionButton onClick={handleSubmit}>{texts.SAVE}</ActionButton>
            </DialogActions>
        </Dialog>
    );
};

export default ManualAddressDialog;
